import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import SEO, { firmJsonLd } from "@/components/SEO";
import { PrimaryCTA, Eyebrow, SectionNum } from "@/components/ui-bits";
import { PRACTICE_AREAS, VISIBLE_PRACTICE_AREAS } from "@/data/practiceAreas";
import { INDUSTRIES } from "@/data/industries";
import { FIRM } from "@/data/firm";
import { ATTORNEYS } from "@/data/attorneys";
import { ArrowUpRight } from "lucide-react";

// Take a curated slice of the firm's most-recent public engagements for the
// Home page grid. Sourced from the founding attorney's engagement list.
// Items that reference Aegis (in title, venue, note, or link labels) are
// excluded from the Home rotation.
const _hasAegis = (e) => {
  const bits = [
    e.title, e.venue, e.note,
    ...(e.links || []).map((l) => `${l.label} ${l.href}`),
  ].join(" ").toLowerCase();
  return bits.includes("aegis");
};
const FEATURED_ENGAGEMENTS = ATTORNEYS[0].engagements.filter((e) => !_hasAegis(e)).slice(0, 4);

export default function Home() {
  return (
    <Layout>
      <SEO
        title="Counsel for Businesses and Federal Regulatory Matters"
        description="Corporate and regulatory counsel for the industries the federal government pays closest attention to — space, defense, semiconductors, and artificial intelligence. Export controls, CFIUS, security clearances, and government contracting under one roof."
        path="/"
        jsonLd={firmJsonLd()}
      />
      {/* Hero — restrained, typographic, no photo */}
      <section className="border-b border-hairline" data-testid="home-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-24 pb-24 lg:pt-32 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-1 hidden lg:block">
              <div className="rule-brass w-12 mt-4" />
            </div>
            <div className="lg:col-span-11">
              <Eyebrow>Shelton Law PLLC</Eyebrow>
              <h1 className="display-1 text-ink mt-6 max-w-5xl animate-fade-up">
                Corporate and regulatory counsel for the industries the federal government pays closest attention to.
              </h1>
              <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                <p className="lg:col-span-7 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
                  Space, defense, semiconductors, and artificial intelligence — sectors where a routine transaction can pull in export controls, CFIUS review, security clearances, and government contracting rules at once. The firm handles all of it under one roof.
                </p>
                <div className="lg:col-span-5 lg:flex lg:items-end lg:justify-end">
                  <PrimaryCTA to="/contact" testId="hero-cta-primary">Contact the Firm</PrimaryCTA>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Import icons used by the Compliance Tools cards. */}
      {/* Compliance Tools band — moved up to sit directly under the hero
          because it's the firm's clearest differentiator. Cards use a
          filled brass icon, a CFR-citation pill, and a hover lift with a
          brass left-stripe to feel more like premium interactive tiles
          than passive text blocks. */}
      <section className="border-b border-hairline bg-ivory-alt" data-testid="home-tools">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-12">
            <div className="lg:col-span-5">
              <Eyebrow>Export Control Tools</Eyebrow>
              <h2 className="display-2 text-ink mt-5 leading-tight">Free tools the firm built and maintains.</h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="text-ink-soft text-[1.0625rem] leading-relaxed">
                Full-text mirrors of the Commerce Control List and the U.S. Munitions List, kept in sync with eCFR, plus a Country Chart–driven license-requirement checker. Built for the firm&apos;s own casework and made public so anyone doing export compliance can use them.
              </p>
            </div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { to: "/ccl", name: "CCL Navigator",
                desc: "A cleaner way to browse the Commerce Control List. Every ECCN is re-indexed by Category, Part, and Product Group, with hierarchical indentation and continuous vertical guides that make deep sub-paragraphs (a.5.b.2.a.1) legible at a glance — instead of eCFR's dense block text." },
              { to: "/usml", name: "USML Navigator",
                desc: "A cleaner way to browse the U.S. Munitions List. All 21 ITAR categories are split by top-level paragraph (a)–(x), each on its own page, with the same indent guides that make nested sub-paragraphs readable without hunting through pages of undifferentiated regulatory text." },
              { to: "/tools/license-check", name: "EAR License Checker",
                desc: "Pick an ECCN and a destination. Matches Reasons for Control against the Commerce Country Chart with paragraph-specific follow-ups and hard-stops for embargoed destinations." },
            ].map(({ to, name, desc }) => (
              <li key={to}>
                <Link
                  to={to}
                  className="group relative block h-full bg-ivory border border-hairline p-8 pl-9 transition-all duration-300
                             hover:-translate-y-1 hover:shadow-[0_18px_40px_-12px_rgba(28,36,50,0.18)] hover:border-brass"
                  data-testid={`home-tool-${to.replace(/^\//, "").replace(/\//g, "-")}`}
                >
                  {/* Left brass stripe — expands on hover */}
                  <span className="absolute left-0 top-0 bottom-0 w-1 bg-brass-deep group-hover:w-1.5 transition-all duration-300" aria-hidden="true" />

                  <h3 className="font-serif text-[1.75rem] leading-tight text-ink group-hover:text-brass-deep transition-colors">{name}</h3>
                  <p className="mt-4 text-ink-soft text-[14px] leading-relaxed">{desc}</p>

                  {/* CTA row */}
                  <span className="inline-flex items-center gap-2 mt-8 text-[11px] uppercase tracking-[0.24em] font-semibold text-ink group-hover:text-brass transition-colors border-b border-brass pb-1">
                    Open tool
                    <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.25} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Industries panel — sector-first framing, dark, sits below the tools band. */}
      <section className="bg-ink text-ivory" data-testid="home-industries">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-4">
              <p className="text-[10px] uppercase tracking-[0.28em] text-brass-light font-semibold">Industries</p>
              <h2 className="font-serif text-4xl sm:text-5xl leading-tight tracking-tight mt-4">Where the firm concentrates.</h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <p className="text-ivory/70 text-[1.0625rem] leading-relaxed">
                Four industries where a single transaction routinely touches export controls, CFIUS, security clearances, and government contracting. Every practice area on this site is here because it keeps showing up in these sectors.
              </p>
            </div>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ivory/10" data-testid="home-industries-grid">
            {INDUSTRIES.filter((i) => !i.hidden).map((ind) => (
              <li key={ind.slug} className="bg-ink">
                <Link
                  to={`/industries/${ind.slug}`}
                  className="group block p-8 h-full hover:bg-white/[0.03] transition-colors"
                  data-testid={`home-industry-${ind.slug}`}
                >
                  <p className="section-num text-brass-light">{ind.number || "—"}</p>
                  <h3 className="font-serif text-2xl text-ivory mt-4 group-hover:text-brass-light transition-colors">{ind.name}</h3>
                  <p className="mt-3 text-ivory/70 text-[13px] leading-relaxed">{ind.kicker}</p>
                  <span className="inline-flex items-center gap-1 mt-6 text-[10px] uppercase tracking-[0.24em] text-brass-light font-semibold">
                    Explore <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Practice areas index — numbered, list-like */}
      <section className="bg-ivory border-b border-hairline" data-testid="home-practice-areas">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-4">
              <Eyebrow>Practice Areas</Eyebrow>
              <h2 className="display-2 text-ink mt-5">
                What the firm does.
              </h2>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              <p className="text-ink-soft text-[1.0625rem] leading-relaxed">
                Nine practice areas, chosen because they are the ones that keep showing up together in the industries the firm serves. A single transaction can require an M&amp;A closing, a CFIUS notice, an export license analysis, and a facility clearance. Clients are often best served by keeping these matters under one roof.
              </p>
            </div>
          </div>

          <ul className="border-t border-hairline" data-testid="home-areas-list">
            {VISIBLE_PRACTICE_AREAS.map((p) => (
              <li key={p.slug} className="border-b border-hairline">
                <Link
                  to={`/services/${p.slug}`}
                  className="group grid grid-cols-12 gap-6 py-7 lg:py-9 items-baseline hover:bg-ivory-alt transition-colors -mx-3 px-3"
                  data-testid={`home-area-${p.slug}`}
                >
                  <SectionNum className="col-span-2 sm:col-span-1 self-start pt-3">{p.number}</SectionNum>
                  <div className="col-span-10 sm:col-span-7 lg:col-span-6">
                    <h3 className="font-serif text-2xl sm:text-3xl text-ink leading-tight group-hover:text-brass transition-colors">
                      {p.name}
                    </h3>
                  </div>
                  <div className="hidden sm:block col-span-3 lg:col-span-4 text-ink-soft text-sm leading-relaxed">
                    {p.kicker}
                  </div>
                  <div className="hidden lg:flex col-span-1 justify-end self-center">
                    <ArrowUpRight className="w-5 h-5 text-brass opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Attorney / about teaser */}
      <section className="border-b border-hairline" data-testid="home-attorney">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>The Attorney</Eyebrow>
            <p className="section-num mt-1">06</p>
            <div className="mt-8 max-w-xs">
              <img
                src="https://customer-assets.emergentagent.com/job_dynasty-trust-tn/artifacts/77411ad1_Jack%20Headshot.jpeg"
                alt="Attorney Jack Shelton"
                className="w-full h-auto grayscale border border-hairline"
                data-testid="home-attorney-portrait"
              />
              <p className="mt-4 text-[11px] uppercase tracking-[0.24em] text-ink-soft font-semibold">Jack Shelton</p>
              <p className="text-[11px] uppercase tracking-[0.24em] text-brass-deep">Founding Attorney</p>
            </div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="display-2 text-ink leading-tight max-w-3xl">
              Jack Shelton — the attorney of record on every matter.
            </h2>
            <div className="mt-8 prose-law max-w-3xl">
              <p>
                Substantial experience in the maritime, defense, and commercial space industries, with a focus on cross-border transactions and matters that live at the intersection of a commercial deal and a federal regulatory regime. Admitted in Tennessee. Co-founder of Aegis Space Law and the Association of Commercial Space Professionals; lead instructor for the Export Compliance Training Institute; regular lecturer at universities and startup accelerators.
              </p>
            </div>
            <div className="mt-10">
              <Link to="/about" className="inline-flex items-center gap-2 text-ink border-b border-brass pb-1 hover:text-brass transition-colors text-[11px] tracking-[0.24em] uppercase font-semibold" data-testid="home-about-cta">
                Full Biography <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Speaking & Publications — curated slice from the firm's engagement list */}
      <section className="border-b border-hairline" data-testid="home-engagements">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Eyebrow>Speaking &amp; Publications</Eyebrow>
            <SectionNum className="mt-1">07</SectionNum>
            <h2 className="display-2 text-ink mt-5 leading-tight">Selected engagements.</h2>
            <p className="mt-6 text-ink-soft text-[1.0625rem] leading-relaxed max-w-md">
              Seminars, e-courses, podcasts, and lectures on the topics that come up in the firm&apos;s practice.
            </p>
            <div className="mt-8">
              <Link
                to="/people/jack-shelton"
                className="inline-flex items-center gap-2 text-ink border-b border-brass pb-1 hover:text-brass transition-colors text-[11px] tracking-[0.24em] uppercase font-semibold"
                data-testid="home-engagements-all-link"
              >
                Full List <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2} />
              </Link>
            </div>
          </div>
          <div className="lg:col-span-8">
            <ul className="border-t border-hairline">
              {FEATURED_ENGAGEMENTS.map((e, i) => (
                <li key={i} className="border-b border-hairline py-6" data-testid={`home-engagement-${i}`}>
                  <div className="grid grid-cols-12 gap-4 items-baseline">
                    <SectionNum className="col-span-2 sm:col-span-1 self-start pt-1.5">{String(i + 1).padStart(2, "0")}</SectionNum>
                    <div className="col-span-10 sm:col-span-8">
                      <h3 className="font-serif italic text-xl lg:text-2xl text-ink leading-snug">{e.title}</h3>
                      {(e.venue || e.date) && (
                        <p className="mt-2 text-ink-soft text-sm leading-relaxed">
                          {e.venue}{e.venue && e.date ? " · " : ""}{e.date}
                          {e.note ? <> · <span className="italic">{e.note.replace(/\.$/, "")}</span></> : null}
                        </p>
                      )}
                    </div>
                    <div className="hidden sm:flex col-span-3 justify-end items-baseline text-[11px] tracking-[0.22em] uppercase font-semibold gap-3">
                      {e.links?.[0] && (
                        <a
                          href={e.links[0].href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brass border-b border-brass pb-0.5 hover:text-brass-deep transition-colors inline-flex items-center gap-1"
                          data-testid={`home-engagement-link-${i}`}
                        >
                          {e.links[0].label} <ArrowUpRight className="w-3 h-3" strokeWidth={2} />
                        </a>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Careers hook temporarily hidden per firm direction. */}

      {/* Contact CTA */}
      <section className="bg-ink text-ivory" data-testid="home-cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-6">Engagement Inquiries</p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight tracking-tight">
              A quick email is the fastest way to see whether the firm is the right fit for your matter.
            </h2>
            <p className="mt-6 text-ivory/70 text-[15px] leading-relaxed max-w-xl">
              Include the type of transaction or filing, the countries involved (if any), and your target timeline. Messages are ordinarily returned within one business day.
            </p>
          </div>
          <div className="lg:col-span-5 lg:text-right space-y-3">
            <p className="text-ivory/70 text-xs tracking-[0.2em] uppercase">Email</p>
            <a href={FIRM.emailHref} className="block font-serif text-3xl text-ivory hover:text-brass-light transition-colors" data-testid="home-cta-email">
              {FIRM.email}
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
}
